import { generateClientImportId, type PartitionDownload } from "./externalRegisterImportCsv";
import type { ExternalRegisterImportDraft } from "./externalRegisterImportDraft";
import type { ExternalRegisterImportHistorySummary } from "../history/externalRegisterImportHistory";
import { createDefaultImportMapping, mergeReviewableCommitErrors, type ImportStage } from "./externalRegisterImportModel";
import {
  EMPTY_IMPORT_MAPPING,
  type ImportCommitResponse,
  type ImportMapping,
  type ImportParseResponse,
  type ImportPreviewRow,
  type ImportReviewFilter,
  type ImportValidateResponse,
} from "./externalRegisterImportTypes";

export type ExternalRegisterImportState = {
  clientImportId: string;
  csvText: string;
  sourceFileName: string | null;
  sourceFileSize: number | null;
  hasHeader: boolean | null;
  mapping: ImportMapping;
  parseResponse: ImportParseResponse | null;
  validateResponse: ImportValidateResponse | null;
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
  activeFilter: ImportReviewFilter;
  page: number;
  isReadingCsvFile: boolean;
  isParsing: boolean;
  isValidating: boolean;
  commitResponses: ImportCommitResponse[];
  isCommitting: boolean;
  partitionDownloads: PartitionDownload[];
  errorMessage: string | null;
  activeStage: ImportStage;
  historySummaries: ExternalRegisterImportHistorySummary[];
};

export type ExternalRegisterImportAction =
  | { type: "reset" }
  | { type: "setCsvText"; csvText: string }
  | { type: "loadFileStart" }
  | { type: "loadFileSuccess"; csvText: string; sourceFileName: string; sourceFileSize: number }
  | { type: "loadFileFailure"; errorMessage: string }
  | { type: "setHeaderMode"; hasHeader: boolean }
  | { type: "setMappingField"; field: keyof ImportMapping; value: string }
  | { type: "setActiveStage"; activeStage: ImportStage }
  | { type: "setActiveFilter"; activeFilter: ImportReviewFilter }
  | { type: "setPage"; page: number | ((current: number) => number) }
  | { type: "setErrorMessage"; errorMessage: string | null }
  | { type: "parseStart" }
  | { type: "parseSuccess"; response: ImportParseResponse }
  | { type: "parseFailure"; errorMessage: string }
  | { type: "validateStart" }
  | { type: "validateSuccess"; response: ImportValidateResponse }
  | { type: "validateFailure"; errorMessage: string }
  | { type: "commitStart" }
  | { type: "commitProgress"; response: ImportCommitResponse }
  | { type: "commitFailure"; errorMessage: string }
  | { type: "commitCompletedWithErrors"; response: ImportCommitResponse }
  | { type: "setPartitionDownloads"; partitionDownloads: PartitionDownload[] }
  | { type: "setHistorySummaries"; historySummaries: ExternalRegisterImportHistorySummary[] }
  | { type: "clearSensitiveDraftAfterCommit" }
  | { type: "approveRow"; rowId: string }
  | { type: "approveRows"; rowIds: string[] }
  | { type: "excludeRow"; rowId: string }
  | { type: "excludeRows"; rowIds: string[] }
  | { type: "selectCandidate"; rowId: string; patientId: string };

export const createResetImportState = (): Omit<ExternalRegisterImportState, "historySummaries"> => ({
  clientImportId: generateClientImportId(),
  csvText: "",
  sourceFileName: null,
  sourceFileSize: null,
  hasHeader: null,
  mapping: { ...EMPTY_IMPORT_MAPPING },
  parseResponse: null,
  validateResponse: null,
  previewRows: [],
  approvedRowIds: new Set(),
  excludedRowIds: new Set(),
  selectedPatientIds: {},
  activeFilter: "needsReview",
  page: 0,
  isReadingCsvFile: false,
  isParsing: false,
  isValidating: false,
  commitResponses: [],
  isCommitting: false,
  partitionDownloads: [],
  errorMessage: null,
  activeStage: "source",
});

export const createInitialImportState = ({
  restoredDraft,
  historySummaries,
}: {
  restoredDraft: ExternalRegisterImportDraft | null;
  historySummaries: ExternalRegisterImportHistorySummary[];
}): ExternalRegisterImportState => ({
  ...createResetImportState(),
  clientImportId: restoredDraft?.clientImportId ?? generateClientImportId(),
  csvText: restoredDraft?.csvText ?? "",
  sourceFileName: restoredDraft?.sourceFileName ?? null,
  sourceFileSize: restoredDraft?.sourceFileSize ?? null,
  hasHeader: restoredDraft?.hasHeader ?? null,
  mapping: restoredDraft?.mapping ?? { ...EMPTY_IMPORT_MAPPING },
  parseResponse: restoredDraft?.parseResponse ?? null,
  validateResponse: restoredDraft?.validateResponse ?? null,
  previewRows: restoredDraft?.previewRows ?? [],
  approvedRowIds: new Set(restoredDraft?.approvedRowIds ?? []),
  excludedRowIds: new Set(restoredDraft?.excludedRowIds ?? []),
  selectedPatientIds: restoredDraft?.selectedPatientIds ?? {},
  historySummaries,
});

export const externalRegisterImportReducer = (
  state: ExternalRegisterImportState,
  action: ExternalRegisterImportAction,
): ExternalRegisterImportState => {
  switch (action.type) {
    case "reset":
      return { ...createResetImportState(), historySummaries: state.historySummaries };
    case "setCsvText":
      return {
        ...state,
        csvText: action.csvText,
        sourceFileName: null,
        sourceFileSize: null,
      };
    case "loadFileStart":
      return { ...state, isReadingCsvFile: true, errorMessage: null };
    case "loadFileSuccess":
      return {
        ...state,
        csvText: action.csvText,
        sourceFileName: action.sourceFileName,
        sourceFileSize: action.sourceFileSize,
        isReadingCsvFile: false,
      };
    case "loadFileFailure":
      return { ...state, isReadingCsvFile: false, errorMessage: action.errorMessage };
    case "setHeaderMode":
      return { ...state, hasHeader: action.hasHeader };
    case "setMappingField":
      return {
        ...state,
        mapping: {
          ...state.mapping,
          [action.field]: action.value === "" ? null : Number(action.value),
        },
      };
    case "setActiveStage":
      return { ...state, activeStage: action.activeStage };
    case "setActiveFilter":
      return { ...state, activeFilter: action.activeFilter, page: 0 };
    case "setPage":
      return { ...state, page: typeof action.page === "function" ? action.page(state.page) : action.page };
    case "setErrorMessage":
      return { ...state, errorMessage: action.errorMessage };
    case "parseStart":
      return { ...state, isParsing: true, errorMessage: null };
    case "parseSuccess":
      return {
        ...state,
        parseResponse: action.response,
        hasHeader: action.response.hasHeaderGuess,
        mapping: createDefaultImportMapping(action.response.columns),
        validateResponse: null,
        previewRows: [],
        commitResponses: [],
        activeStage: "parse",
        isParsing: false,
      };
    case "parseFailure":
      return { ...state, isParsing: false, errorMessage: action.errorMessage };
    case "validateStart":
      return { ...state, isValidating: true, errorMessage: null };
    case "validateSuccess":
      return {
        ...state,
        validateResponse: action.response,
        previewRows: action.response.preview,
        activeFilter: action.response.needsReviewRows > 0 ? "needsReview" : "ready",
        page: 0,
        commitResponses: [],
        activeStage: "review",
        isValidating: false,
      };
    case "validateFailure":
      return { ...state, isValidating: false, errorMessage: action.errorMessage };
    case "commitStart":
      return { ...state, isCommitting: true, errorMessage: null, commitResponses: [] };
    case "commitProgress":
      return {
        ...state,
        commitResponses: [...state.commitResponses, action.response],
        isCommitting: action.response.status === "committing",
      };
    case "commitFailure":
      return { ...state, isCommitting: false, errorMessage: action.errorMessage };
    case "commitCompletedWithErrors":
      return {
        ...state,
        previewRows: mergeReviewableCommitErrors(state.previewRows, action.response),
        activeFilter: "needsReview",
      };
    case "setPartitionDownloads":
      return { ...state, partitionDownloads: action.partitionDownloads };
    case "setHistorySummaries":
      return { ...state, historySummaries: action.historySummaries };
    case "clearSensitiveDraftAfterCommit":
      return { ...state, csvText: "", sourceFileName: null, sourceFileSize: null, isCommitting: false };
    case "approveRow": {
      const approvedRowIds = new Set(state.approvedRowIds);
      const excludedRowIds = new Set(state.excludedRowIds);
      if (approvedRowIds.has(action.rowId)) {
        approvedRowIds.delete(action.rowId);
      } else {
        approvedRowIds.add(action.rowId);
        excludedRowIds.delete(action.rowId);
      }
      return { ...state, approvedRowIds, excludedRowIds };
    }
    case "approveRows": {
      const approvedRowIds = new Set(state.approvedRowIds);
      const excludedRowIds = new Set(state.excludedRowIds);
      action.rowIds.forEach((rowId) => {
        approvedRowIds.add(rowId);
        excludedRowIds.delete(rowId);
      });
      return { ...state, approvedRowIds, excludedRowIds };
    }
    case "excludeRow": {
      const excludedRowIds = new Set(state.excludedRowIds);
      const approvedRowIds = new Set(state.approvedRowIds);
      if (excludedRowIds.has(action.rowId)) {
        excludedRowIds.delete(action.rowId);
      } else {
        excludedRowIds.add(action.rowId);
        approvedRowIds.delete(action.rowId);
      }
      return { ...state, approvedRowIds, excludedRowIds };
    }
    case "excludeRows": {
      const excludedRowIds = new Set(state.excludedRowIds);
      const approvedRowIds = new Set(state.approvedRowIds);
      action.rowIds.forEach((rowId) => {
        excludedRowIds.add(rowId);
        approvedRowIds.delete(rowId);
      });
      return { ...state, approvedRowIds, excludedRowIds };
    }
    case "selectCandidate":
      return {
        ...state,
        selectedPatientIds: {
          ...state.selectedPatientIds,
          [action.rowId]: action.patientId,
        },
      };
    default:
      return state;
  }
};
